import React from 'react';
import * as styles from './CardStack.style';
import CardStackItem, { CardStackItemProps } from './components/item/CardStackItem';
import CardStackContext from './CardStackContext';

export type CardStackProps = React.PropsWithChildren<{
  initialOpenPosition?: number,
}> & React.AriaAttributes;

type CardStackComposition = {
  Item: typeof CardStackItem,
};

const CardStack: React.FC<CardStackProps> & CardStackComposition = ({
  initialOpenPosition,
  children,
  'aria-label': ariaLabel = 'Lista di contenuti',
  ...rest
}) => {
  const [openPosition, setOpenPosition] = React.useState<number | undefined>(initialOpenPosition);

  const total = React.useMemo(
    () => React.Children.toArray(children).length,
    [children],
  );

  const contextValue = React.useMemo(() => ({
    openPosition,
    updateOpenPosition: (position) => () => setOpenPosition(position),
  }), [openPosition]);

  const getOverlappingIndex = React.useCallback(
    (position: number) => () => {
      if (position === openPosition) {
        return total;
      }

      return total - position;
    },
    [total, openPosition],
  );

  return (
    <div
      css={styles.base}
      aria-label={ariaLabel}
      {...rest}
    >
      <CardStackContext.Provider value={contextValue}>
        {React.Children.map(children, (child: React.ReactElement<CardStackItemProps>, i) => {
          if (child?.type !== CardStackItem) {
            throw new Error('The component children must be an array of CardStack.Item');
          }

          return (
            <div
              css={styles.item}
              $zIndex={getOverlappingIndex(i)}
            >
              {React.cloneElement(child, {
                position: i,
                'aria-label': `${ariaLabel} (Singolo contenuto)`,
              })}
            </div>
          );
        })}
      </CardStackContext.Provider>
    </div>
  );
};

CardStack.Item = CardStackItem;

export default CardStack;
